import React from 'react';
import { isEmpty, get, map } from 'lodash';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import gql from 'graphql-tag';
import { FormattedMessage } from 'react-intl';
import { format } from 'date-fns';

import { Loader, Grid } from 'semantic-ui-react';

import { Table } from '../../../../components';

export const GET_CLIENT_RATING_QUERY = gql`
    query getClientRating($salon: ID!) {
        viewer {
            id
            ... on Administrator {
                salon(id: $salon) {
                    id
                    clientRating {
                        id
                        firstName
                        lastName
                        phone
                        totalVisits
                        firstAppointment
                        lastAppointment
                    }
                }
            }
        }
    }
`;

const EmptyMessage = () => (
    <Table.Row className="transparent">
        <Table.Cell colSpan={5} textAlign="center">
            <FormattedMessage
                id="pages.report.ReportTable.noRating"
                defaultMessage="You don't have client rating yet"
            />
        </Table.Cell>
    </Table.Row>
);

const RatingRow = ({ client }) => (
    <Table.Row>
        <Table.Cell>
            {`${client.firstName} ${client.lastName || ''}`}
        </Table.Cell>
        <Table.Cell>{client.phone}</Table.Cell>
        <Table.Cell textAlign="center">{client.totalVisits}</Table.Cell>
        <Table.Cell textAlign="right">{client.firstAppointment}</Table.Cell>
        <Table.Cell textAlign="right">{client.lastAppointment}</Table.Cell>
    </Table.Row>
);

const RatingTable = ({ clientRating }) => (
    <Table celled selectable striped fixed>
        <Table.Header>
            <Table.Row>
                <Table.HeaderCell>
                    <FormattedMessage
                        id="pages.report.ReportTable.client"
                        defaultMessage="Client"
                    />
                </Table.HeaderCell>
                <Table.HeaderCell>
                    <FormattedMessage
                        id="pages.report.ReportTable.rating.phone"
                        defaultMessage="Phone"
                    />
                </Table.HeaderCell>
                <Table.HeaderCell textAlign="center">
                    <FormattedMessage
                        id="pages.report.ReportTable.rating.totalVisists"
                        defaultMessage="Total visits"
                    />
                </Table.HeaderCell>
                <Table.HeaderCell textAlign="right">
                    <FormattedMessage
                        id="pages.report.ReportTable.rating.firstVisit"
                        defaultMessage="First visit"
                    />
                </Table.HeaderCell>
                <Table.HeaderCell textAlign="right">
                    <FormattedMessage
                        id="pages.report.ReportTable.rating.lastVisit"
                        defaultMessage="Last visit"
                    />
                </Table.HeaderCell>
            </Table.Row>
        </Table.Header>
        <Table.Body>
            {isEmpty(clientRating) ? (
                <EmptyMessage />
            ) : (
                clientRating.map(
                    client =>
                        !!client && (
                            <RatingRow key={client.id} client={client} />
                        ),
                )
            )}
        </Table.Body>
    </Table>
);

function ClientRatingTable() {
    const salon = useSelector(({ user }) => user.get('salon'));

    const { loading, data } = useQuery(GET_CLIENT_RATING_QUERY, {
        variables: { salon },
        fetchPolicy: 'cache-and-network',
    });

    if (loading) return <Loader size="big" inline="centered" active />;

    const clientRating =
        !loading && data && data.viewer
            ? map(get(data.viewer, 'salon.clientRating'), i => ({
                  id: i.id,
                  firstName: i.firstName,
                  lastName: i.lastName || '',
                  phone: i.phone || '',
                  totalVisits: i.totalVisits,
                  firstAppointment: format(new Date(i.firstAppointment), 'dd.MM.yyyy'),
                  lastAppointment: format(new Date(i.lastAppointment), 'dd.MM.yyyy'),
              }))
            : [];

    return (
        <Grid columns={1} padded>
            <Grid.Row>
                <Grid.Column>
                    <RatingTable clientRating={clientRating} />
                </Grid.Column>
            </Grid.Row>
        </Grid>
    );
}

export default ClientRatingTable;
